import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import HttpService from '../../../services/HttpService';
import FormSkeleton from '../../../components/Skeletons/FormSkeleton';
import { Form, Formik } from 'formik';
import { initialValuesEditCollection } from '../Registrations/types';
import { editCollectionValidation } from '../Registrations/validationSchema';
import FormStepperBuilder from '../../../components/form_v2/highLevelComponents/FormStepperBuilder';
import FormActionButtonLayout from '../../../components/layout/Form/FormActionButtonLayout';
import { Button } from '@mui/material';
import {
  eliminateOjectNotTruthyValues,
  isSubmitEnabled,
} from '../../../lib/helperFuncs';
import { useSnackbar } from 'notistack';
import AsyncBackdropSubmit from '../../../components/AsyncBackdropSubmit';
import { getCollection } from '../../../lib/fetchers';
import TitlePage from '../../../components/layout/Page/TitlePage';
import { insertCollectionFields } from './static';
import PreviewCollection from './PreviewCollection';

const EditSulloges = () => {
  document.title = 'Επεξεργασία Συλλογής';

  const [omekaResponse, setOmekaResponse] = React.useState<any>({});
  const [isLoadingCollection, setIsLoadingCollection] = React.useState(true);
  const [renderPreview, setRenderPreview] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchCollection = async (collection_id: string) => {
    try {
      setIsLoadingCollection(true);
      const { collection: collection_object } = await getCollection(collection_id);
      setOmekaResponse(collection_object);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCollection(false);
    }
  };

  const onSubmitEdit = async (values: any) => {
    setOpenBackdrop(true);
    try {
      await HttpService.getAxiosClient().put(
        `${process.env.REACT_APP_BACKEND_URL}/collections/${omekaResponse.id}`,
        eliminateOjectNotTruthyValues({ ...values })
      );
      enqueueSnackbar('H καταχώριση συλλογής υποβλήθηκε επιτυχώς', {
        variant: 'success',
      });
      navigate('/admin/collection/dashboard');
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        'H καταχώριση συλλογής απέτυχε, παρακαλώ προσπαθήστε ξανά.',
        { variant: 'error' }
      );
    }
    setOpenBackdrop(false);
  };

  React.useEffect(() => {
    searchParams.delete('edit');
    searchParams.delete('preview');
    setSearchParams(searchParams);

    id && fetchCollection(id);
  }, []);

  // Set confirmation message in case someone wants to refresh or leave the page
  React.useEffect(() => {
    const unloadCallback = (event: any) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', unloadCallback);
    return () => window.removeEventListener('beforeunload', unloadCallback);
  }, []);

  return (
    <>
      <AsyncBackdropSubmit isLoading={openBackdrop}>
        Παρακαλώ περιμένετε μέχρι να ολοκληρωθεί η διαδικασία...
      </AsyncBackdropSubmit>
      <TitlePage
        backButton={false}
        title={
          Object.prototype.hasOwnProperty.call(
            Object.fromEntries([...searchParams]),
            'preview'
          )
            ? 'Προεπισκόπηση Επεξεργασίας Συλλογής'
            : 'Επεξεργασία Συλλογής'
        }
      />
      {isLoadingCollection
        ? (
          <FormSkeleton lines={6} />
        )
        : !renderPreview
          ? (
            <>
              <Formik
                enableReinitialize
                onSubmit={() => undefined}
                initialValues={initialValuesEditCollection(omekaResponse)}
                validationSchema={editCollectionValidation}
              >
                {(formik) => (
                  <>
                    <Form>
                      <FormStepperBuilder
                        formik={formik}
                        form={insertCollectionFields()}
                      />
                      <FormActionButtonLayout>
                        <Button
                          variant='outlined'
                          color='error'
                          onClick={() => navigate('/admin/collection/dashboard')}
                        >
                          Ακύρωση Επεξεργασίας
                        </Button>
                        <Button
                          variant='contained'
                          disabled={isSubmitEnabled(formik?.errors, true)}
                          onClick={() => {
                            setOmekaResponse({ ...formik.values } as any);
                            searchParams.set('preview', 'true');
                            searchParams.delete('edit');
                            setSearchParams(searchParams);
                            setRenderPreview(true);
                          }}
                        >
                          Προεπισκόπηση Καταχώρισης
                        </Button>
                      </FormActionButtonLayout>
                    </Form>
                  </>
                )}
              </Formik>
            </>
          )
          : (
            <PreviewCollection
              onSubmit={onSubmitEdit}
              data={omekaResponse}
              setRenderPreview={setRenderPreview}
              isLoading={isLoadingCollection}
            />
          )}
    </>
  );
};

export default EditSulloges;
